import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { useUIStore } from '@/store/useUIStore';
import { relativeTime, exactTime } from '@/utils/relativeTime';
import { IspPage } from './ispLayout';
import IspPager, { PAGE_SIZE } from './IspPager';
import { RowActions, actionColumn } from './ispSettingsUI';
import {
   archiveIspInvoice,
   decideIspPaymentProof,
   getIspTenant,
   getIspInvoices,
   getIspCustomers,
   createIspPayment,
   type IspInvoiceRow,
   type IspPaymentDraft,
} from '@/api/isp';

/**
 * ISP Business — Invoices.
 *
 * What each customer owes, and what they have paid.
 *
 * ─── OVERDUE IS THE POINT OF THIS SCREEN ───
 * An operator opens it to answer one question: who has not paid? So overdue rows
 * are sorted to the top and counted in the toolbar, rather than being something
 * you find by reading dates in a list sorted by number.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;
/**
 * A bill day, the way somebody reads it aloud: "September 4, 2026".
 *
 * Ray, 2026-09-04: *"the due column the date format should readable April 5,
 * 2026."* It rendered `2026-09-04`, which is a machine's date.
 *
 * ⚠️ FORMATTED IN MANILA, EXPLICITLY. `dueAt` is stored at UTC midnight, so a
 * browser west of Greenwich would render the day BEFORE — the bill day would
 * read differently depending on where the operator opened it.
 */
const day = (d: string) =>
   new Date(d).toLocaleDateString('en-PH', {
      timeZone: 'Asia/Manila',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
   });

const STATUS_LABEL: Record<IspInvoiceRow['status'], string> = {
   unpaid: 'Unpaid',
   partial: 'Part paid',
   paid: 'Paid',
   void: 'Void',
};

/**
 * Midnight in Manila, for a given instant. Everything about a bill day is a
 * DAY, not a moment, and the operator's day is UTC+8.
 */
const manilaMidnight = (when: Date | string) => {
   const d = new Date(when);
   const shifted = new Date(d.getTime() + 8 * 3600000);
   return Date.UTC(
      shifted.getUTCFullYear(),
      shifted.getUTCMonth(),
      shifted.getUTCDate()
   );
};

/**
 * Past its due date and still owing. Not a stored flag — dates move on.
 *
 * 🔴 THE DUE DAY BELONGS TO THE CUSTOMER, ALL OF IT. This compared raw
 * timestamps, and `dueAt` is UTC midnight — which is 8am in Manila. So a bill
 * due TODAY was painted red from breakfast, and this page counted it in
 * "Overdue". Ray, 2026-09-04, seeing it on his own first invoice hours before
 * it was actually late.
 *
 * The same fault was fixed in the Accounts endpoint; this page computes its own
 * in the browser and was missed. Overdue means the due DAY has ended.
 */
const isOverdue = (i: IspInvoiceRow) =>
   i.outstanding > 0 && manilaMidnight(i.dueAt) < manilaMidnight(new Date());

type Filter = 'all' | 'owing' | 'overdue' | 'paid';

const IspInvoices: React.FC = () => {
   const qc = useQueryClient();
   const { handleShow, handleClose } = useUIStore();
   const [filter, setFilter] = useState<Filter>('all');
   const [query, setQuery] = useState('');

   const { data: invoices = [], isLoading } = useQuery({
      queryKey: ['isp-invoices'],
      queryFn: () => getIspInvoices(),
   });
   // The operator's own business details go on every printed invoice: "who do
   // I call" is the first question somebody with no internet has.
   const { data: tenant } = useQuery({
      queryKey: ['isp-tenant'],
      queryFn: getIspTenant,
   });

   const { data: customers = [] } = useQuery({
      queryKey: ['isp-customers'],
      queryFn: getIspCustomers,
   });
   const customerName = (id: string) =>
      customers.find((c) => c.id === id)?.fullName ?? '—';

   const msg = (e: unknown, fallback: string) =>
      (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
      fallback;

   const pay = useMutation({
      mutationFn: (draft: IspPaymentDraft) => createIspPayment(draft),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-invoices'] });
         handleClose();
         // Overpayment is the operator's problem to resolve with the customer,
         // so it is a warning they have to read, not a quiet success.
         if (r.unapplied > 0) toast.warning(r.message);
         else toast.success(r.message);
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not record the payment.')),
   });

   /**
    * The paper copy.
    *
    * A suspended customer has no internet, so they cannot open a portal or read
    * an email — paper and a screenshot are what still work, and that is exactly
    * the moment they most need to know what they owe.
    */
   /**
    * Approve or reject what a customer says they paid.
    *
    * 🔴 Approving takes the SAME path as a payment recorded at the counter —
    * one way money enters the product, whoever started it.
    */
   const decideProof = useMutation({
      mutationFn: (v: {
         id: string;
         decision: 'approved' | 'rejected';
         amount?: number;
         reason?: string;
      }) =>
         decideIspPaymentProof(v.id, v.decision, {
            amount: v.amount,
            reason: v.reason,
         }),
      onSuccess: (r, v) => {
         qc.invalidateQueries({ queryKey: ['isp-invoices'] });
         handleClose();
         toast.success(
            v.decision === 'approved'
               ? (r.message ?? 'Payment recorded.')
               : 'Rejected. The customer will see your reason.'
         );
      },
      onError: (e: unknown) => toast.error(msg(e, 'Could not save that.')),
   });

   const openProof = (i: IspInvoiceRow) => {
      if (!i.pendingProof) return;
      handleShow({
         template: 'isp-proof-review',
         title: `Payment review — ${customerName(i.customer)}`,
         windowType: 'desktop',
         data: {
            customerName: customerName(i.customer),
            invoiceNumber: i.number,
            outstanding: i.outstanding,
            proof: i.pendingProof,
            onCancel: handleClose,
            onApprove: (amount: number) =>
               decideProof.mutate({
                  id: i.pendingProof!.id,
                  decision: 'approved',
                  amount,
               }),
            onReject: (reason: string) =>
               decideProof.mutate({
                  id: i.pendingProof!.id,
                  decision: 'rejected',
                  reason,
               }),
         },
      });
   };

   const archive = useMutation({
      mutationFn: (id: string) => archiveIspInvoice(id, true),
      onSuccess: (r) => {
         qc.invalidateQueries({ queryKey: ['isp-invoices'] });
         toast.success(r.message);
      },
      // The refusal names the amount still owed. Shown verbatim.
      onError: (e: unknown) => toast.error(msg(e, 'Could not archive that.')),
   });

   const openPrint = (i: IspInvoiceRow) =>
      handleShow({
         template: 'isp-invoice-print',
         title: `${i.number} — ${customerName(i.customer)}`,
         windowType: 'desktop',
         data: {
            invoice: i,
            customer: customers.find((c) => c.id === i.customer) ?? null,
            tenant: tenant ?? null,
            onClose: handleClose,
         },
      });

   const openPayment = (i: IspInvoiceRow) =>
      handleShow({
         template: 'isp-payment-form',
         title: `Record payment — ${customerName(i.customer)}`,
         windowType: 'desktop',
         data: {
            customerId: i.customer,
            customerName: customerName(i.customer),
            outstanding: i.outstanding,
            onCancel: handleClose,
            onSave: (draft: IspPaymentDraft) => pay.mutate(draft),
         },
      });

   const counts = useMemo(() => {
      const c = { all: 0, owing: 0, overdue: 0, paid: 0 };
      invoices.forEach((i) => {
         if (i.pendingProof) return; // counted in its own table above
         c.all++;
         if (i.status === 'paid') c.paid++;
         if (i.outstanding > 0) c.owing++;
         if (isOverdue(i)) c.overdue++;
      });
      return c;
   }, [invoices]);

   const owed = useMemo(
      () => invoices.reduce((s, i) => s + i.outstanding, 0),
      [invoices]
   );

   /**
    * Bills where somebody says they have paid, waiting on the operator.
    *
    * 🔴 THEIR OWN TABLE, ABOVE THE LIST. Ray, 2026-09-03: *"the payment review
    * it doesnt looks or indicate its clickable or reviewable… lets separate a
    * table for the invoices that has submitted a payment to make more obvious
    * and action needed look."* A pill in a status column reads as a state, not
    * as a job — and this is the one row on the page that is waiting on a
    * person. Same shape as the applications waiting on the Customers page.
    */
   const claims = useMemo(
      () => invoices.filter((i) => i.pendingProof),
      [invoices]
   );

   /**
    * 🔴 RESET ON EVERY FILTER CHANGE. Ray, 2026-09-03: *"make sure accounts,
    * customers, billing has pagination."* Without the reset, filtering from
    * 300 rows down to 4 while on page 6 shows an empty table and an operator
    * who believes the filter found nothing.
    */
   const [page, setPage] = useState(0);

   const shown = useMemo(() => {
      const q = query.trim().toLowerCase();
      const list = invoices.filter((i) => {
         // Never in both tables at once — a job listed twice is a job done
         // twice, or missed because it looked handled.
         if (i.pendingProof) return false;
         if (filter === 'owing' && i.outstanding <= 0) return false;
         if (filter === 'overdue' && !isOverdue(i)) return false;
         if (filter === 'paid' && i.status !== 'paid') return false;
         if (!q) return true;
         return (
            customerName(i.customer).toLowerCase().includes(q) ||
            i.number.toLowerCase().includes(q)
         );
      });
      // Overdue first — the whole reason somebody opens this page.
      return [...list].sort((a, b) => {
         const ao = isOverdue(a) ? 0 : 1;
         const bo = isOverdue(b) ? 0 : 1;
         if (ao !== bo) return ao - bo;
         return new Date(b.dueAt).getTime() - new Date(a.dueAt).getTime();
      });
   }, [invoices, filter, query, customers]);

   const paged = useMemo(
      () => shown.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
      [shown, page]
   );

   // A filter that narrows the list must take the reader back to its start,
   // or they land on a page that no longer exists and read it as "nothing
   // found".
   useEffect(() => {
      setPage(0);
   }, [shown.length]);

   return (
      <IspPage>
         <TopRow>
            <Title>Invoices</Title>
            {/* 🔴 "Generate this month" is gone. Ray, 2026-09-02: *"this is
                also tempory? so removed that. the system auto generate it
                before due date right?"* Right — the hourly run raises every
                invoice on its due day, and a button that duplicates a job the
                system already does is a button that only ever confuses. */}
         </TopRow>
         <Note>
            Bills are created per customer per due day — somebody with two
            branches installed on the same day gets one bill with two lines.
            Suspended and disconnected accounts are not billed.
         </Note>

         {invoices.length > 0 && (
            <Summary>
               <Stat>
                  <Num $tone={counts.overdue ? 'bad' : 'plain'}>{counts.overdue}</Num>
                  <Lbl>Overdue</Lbl>
               </Stat>
               <Stat>
                  <Num $tone={counts.owing ? 'warn' : 'plain'}>{counts.owing}</Num>
                  <Lbl>Still owing</Lbl>
               </Stat>
               <Stat>
                  <Num $tone="plain">{peso(owed)}</Num>
                  <Lbl>Amount due</Lbl>
               </Stat>
            </Summary>
         )}

         {claims.length > 0 && (
            <Claims>
               <ClaimsHead>
                  <ClaimsTitle>
                     {claims.length} payment{claims.length === 1 ? '' : 's'} to
                     check
                  </ClaimsTitle>
                  <ClaimsSub>
                     A customer says they have paid. Nothing is credited, and
                     nobody is reconnected, until you open it.
                  </ClaimsSub>
               </ClaimsHead>
               <TableWrap>
                  <Table>
                     <thead>
                        <tr>
                           <Th>Invoice</Th>
                           <Th>Customer</Th>
                           <Th>They say they sent</Th>
                           <Th>Amount due</Th>
                           <Th>Actions</Th>
                        </tr>
                     </thead>
                     <tbody>
                        {claims.map((i) => (
                           <tr key={i.id}>
                              <Td $mono>{i.number}</Td>
                              <Td $bold>{customerName(i.customer)}</Td>
                              <Td $bold>{peso(i.pendingProof!.amount)}</Td>
                              <Td>{peso(i.outstanding)}</Td>
                              <Td>
                                 <RowActions>
                                    <Review
                                       type="button"
                                       onClick={() => openProof(i)}
                                    >
                                       Review payment
                                    </Review>
                                    <LinkBtn
                                       type="button"
                                       onClick={() => openPrint(i)}
                                    >
                                       Print
                                    </LinkBtn>
                                 </RowActions>
                              </Td>
                           </tr>
                        ))}
                     </tbody>
                  </Table>
               </TableWrap>
            </Claims>
         )}

         <Bar>
            <Search
               value={query}
               placeholder="Search customer or invoice number"
               onChange={(e) => setQuery(e.target.value)}
            />
            {(['all', 'overdue', 'owing', 'paid'] as Filter[]).map((f) => (
               <Chip key={f} type="button" $on={filter === f} onClick={() => setFilter(f)}>
                  {f === 'all' ? 'All' : f[0].toUpperCase() + f.slice(1)} ({counts[f]})
               </Chip>
            ))}
         </Bar>

         {isLoading && <SkeletonLines lines={3} />}

         {!isLoading && invoices.length === 0 && (
            <EmptyState>
               <EmptyBig>No invoices yet</EmptyBig>
               <EmptySub>
                  Bills are raised automatically on each customer&apos;s due
                  day. Nothing to press.
               </EmptySub>
            </EmptyState>
         )}

         {!isLoading && invoices.length > 0 && shown.length === 0 && (
            <EmptyNote>Nothing matches that.</EmptyNote>
         )}

         {shown.length > 0 && (
            <>
            <TableWrap>
               <Table>
                  <thead>
                     <tr>
                        <Th>Invoice</Th>
                        <Th>Customer</Th>
                        <Th>For</Th>
                        <Th>Due</Th>
                        {/* When the bill was RAISED. Ray, 2026-09-04: *"the
                            invoice generated should have date created."* Due
                            and created can be the same day, and without both
                            there is no way to tell which you are looking at. */}
                        <Th>Created</Th>
                        <Th>Total</Th>
                        <Th>Amount due</Th>
                        <Th>Status</Th>
                        <Th>Actions</Th>
                     </tr>
                  </thead>
                  <tbody>
                     {paged.map((i) => (
                        <tr key={i.id}>
                           <Td $mono>{i.number}</Td>
                           <Td $bold>{customerName(i.customer)}</Td>
                           <Td $muted>
                              {i.lines.map((l) => l.planName).join(', ') || '—'}
                           </Td>
                           <Td $muted>
                              {day(i.dueAt)}
                              {isOverdue(i) && <Late>overdue</Late>}
                           </Td>
                           {/* Relative, with the exact stamp in the tooltip —
                               "5min ago" is what an operator actually wants
                               from a bill raised while they were looking. */}
                           <Td $muted title={exactTime(i.createdAt)}>
                              {relativeTime(i.createdAt)}
                           </Td>
                           <Td>{peso(i.total)}</Td>
                           <Td $bold={i.outstanding > 0}>
                              {i.outstanding > 0 ? peso(i.outstanding) : '—'}
                           </Td>
                           <Td>
                              {/* No "payment review" pill here any more — a
                                  bill with a waiting claim lives in its own
                                  table above, where it reads as a job. */}
                              <Pill $s={i.status}>{STATUS_LABEL[i.status]}</Pill>
                           </Td>
                           <Td>
                              <RowActions>
                                 {/* The primary action on an unpaid bill, and
                                     coloured like one. Ray, 2026-09-02: *"replace
                                     record paymebt button to pay now with
                                     different color."* "Record payment" read like
                                     admin; taking the money is the job. */}
                                 {i.outstanding > 0 && (
                                    <PayNow type="button" onClick={() => openPayment(i)}>
                                       Pay now
                                    </PayNow>
                                 )}
                                 {/* OUTSIDE the unpaid branch on purpose: a paid
                                     invoice printed is the receipt, and somebody
                                     who paid cash at the office asks for one. */}
                                 <LinkBtn type="button" onClick={() => openPrint(i)}>
                                    Print
                                 </LinkBtn>
                                 {/* Only on a settled bill: the server refuses to
                                     archive one that still owes money, because
                                     hiding a debt is how it gets forgotten. */}
                                 {i.outstanding === 0 ? (
                                    <LinkBtn
                                       type="button"
                                       disabled={archive.isPending}
                                       onClick={() => archive.mutate(i.id)}
                                    >
                                       Archive
                                    </LinkBtn>
                                 ) : null}
                              </RowActions>
                           </Td>
                        </tr>
                     ))}
                  </tbody>
               </Table>
            </TableWrap>
               <IspPager
                  page={page}
                  total={shown.length}
                  onPage={setPage}
                  noun="invoices"
               />
            </>
         )}
      </IspPage>
   );
};

/**
 * Deliberately louder than a status pill.
 *
 * Somebody is waiting on the operator to look. A quiet chip in a column of
 * other quiet chips is a queue nobody works.
 */
const PayNow = styled.button`
   font-family: inherit;
   font-size: 12px;
   font-weight: 800;
   padding: 6px 14px;
   border-radius: 8px;
   border: none;
   background: ${colors.GREEN};
   color: #fff;
   cursor: pointer;
`;

/**
 * The table of claims waiting on the operator.
 *
 * Amber and banded, the same language the Customers page uses for
 * applications waiting — this is a queue of work, not a list of records.
 */
const Claims = styled.section`
   margin-bottom: 18px;
   border: 1px solid #fcd34d;
   border-left: 5px solid #f59e0b;
   border-radius: 14px;
   background: #fffbeb;
   padding: 14px;
`;
const ClaimsHead = styled.div`
   margin-bottom: 12px;
`;
const ClaimsTitle = styled.div`
   font-size: 15px;
   font-weight: 800;
   color: #92400e;
`;
const ClaimsSub = styled.div`
   margin-top: 2px;
   font-size: 13px;
   line-height: 1.5;
   color: #b45309;
`;
/** Says what pressing it does. "Payment review" named a state, not an action. */
const Review = styled.button`
   font-family: inherit;
   font-size: 13px;
   font-weight: 800;
   padding: 8px 14px;
   border-radius: 9px;
   border: none;
   background: #b45309;
   color: #fff;
   cursor: pointer;
   white-space: nowrap;
`;

export default IspInvoices;

const TopRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;
const Title = styled.h1`
   margin: 0;
   font-size: 20px;
`;
const Note = styled.p`
   margin: 0;
   font-size: 12.5px;
   line-height: 1.6;
   color: ${colors.GRAY5};
   max-width: 70ch;
`;
const Summary = styled.div`
   display: flex;
   gap: 26px;
   flex-wrap: wrap;
   padding: 14px 18px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 12px;
   background: ${colors.GRAY14};
`;
const Stat = styled.div`
   text-align: center;
   min-width: 72px;
`;
const Num = styled.div<{ $tone: 'bad' | 'warn' | 'plain' }>`
   font-size: 19px;
   font-weight: 700;
   line-height: 1.1;
   color: ${({ $tone }) =>
      $tone === 'bad' ? colors.DANGER : $tone === 'warn' ? colors.ORANGE : colors.MAIN_DARK};
`;
const Lbl = styled.div`
   margin-top: 2px;
   font-size: 11px;
   color: ${colors.GRAY5};
   white-space: nowrap;
`;
const Bar = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   flex-wrap: wrap;
`;
const Search = styled.input`
   flex: 1 1 240px;
   padding: 8px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
`;
const Chip = styled.button<{ $on: boolean }>`
   padding: 6px 12px;
   border-radius: 20px;
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY12)};
   background: ${({ $on }) => ($on ? colors.PRIMARY : 'transparent')};
   color: ${({ $on }) => ($on ? colors.WHITE : colors.GRAY3)};
`;
const TableWrap = styled.div`
   overflow-x: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const Table = styled.table`
   width: 100%;
   border-collapse: collapse;
   font-size: 13px;
   ${actionColumn}
`;
const Th = styled.th`
   text-align: left;
   padding: 10px 12px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   background: ${colors.GRAY14};
   white-space: nowrap;
`;
const Td = styled.td<{ $bold?: boolean; $muted?: boolean; $mono?: boolean }>`
   padding: 10px 12px;
   border-top: 1px solid ${colors.GRAY13};
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.MAIN_DARK)};
   font-weight: ${({ $bold }) => ($bold ? 600 : 400)};
   font-family: ${({ $mono }) => ($mono ? 'monospace' : 'inherit')};
   white-space: nowrap;
`;
const Late = styled.span`
   margin-left: 8px;
   padding: 1px 7px;
   border-radius: 8px;
   font-size: 10px;
   font-weight: 700;
   background: ${colors.DANGER}18;
   color: ${colors.DANGER};
`;
const Pill = styled.span<{ $s: IspInvoiceRow['status'] }>`
   display: inline-block;
   padding: 2px 10px;
   border-radius: 10px;
   font-size: 11px;
   font-weight: 600;
   background: ${({ $s }) =>
      $s === 'paid' ? `${colors.SUCCESS}18` : $s === 'void' ? colors.GRAY12 : `${colors.ORANGE}18`};
   color: ${({ $s }) =>
      $s === 'paid' ? colors.SUCCESS : $s === 'void' ? colors.GRAY5 : colors.ORANGE};
`;
const Primary = styled.button`
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   border: none;
   border-radius: 8px;
   padding: 8px 14px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   &:disabled {
      opacity: 0.6;
   }
`;
const LinkBtn = styled.button`
   background: transparent;
   color: ${colors.PRIMARY};
   border: 1px solid ${colors.PRIMARY}40;
   border-radius: 8px;
   padding: 5px 12px;
   font-size: 12px;
   font-weight: 700;
   cursor: pointer;
   white-space: nowrap;
`;
const EmptyNote = styled.p`
   margin: 0;
   padding: 20px;
   text-align: center;
   font-size: 13px;
   color: ${colors.GRAY5};
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;
const EmptyState = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   padding: 40px 22px;
   text-align: center;
   border: 1px dashed ${colors.GRAY12};
   border-radius: 12px;
`;
const EmptyBig = styled.div`
   font-size: 15px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;
const EmptySub = styled.p`
   margin: 0 0 6px;
   max-width: 440px;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.GRAY5};
`;
